import clsx from 'clsx'
import React from 'react'

// ============================================================================
// Empty State Component
// A placeholder for when there's no content to display
// ============================================================================

interface EmptyStateProps {
  /** Icon to display */
  icon?: React.ReactNode
  /** Title text */
  title?: string
  /** Description text */
  description?: string
  /** Action button/link */
  action?: React.ReactNode
  /** Additional CSS classes */
  className?: string
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div className={clsx('flex flex-col items-center justify-center gap-4 py-12', className)}>
      {icon && (
        <div className="text-foreground-subtle">
          {icon}
        </div>
      )}
      {title && (
        <p className="text-foreground-muted font-medium">{title}</p>
      )}
      {description && (
        <p className="text-foreground-subtle text-sm text-center max-w-sm">{description}</p>
      )}
      {action}
    </div>
  )
}

// ============================================================================
// Loading State Component
// A loading indicator with optional message (horizontal inline format, centered)
// ============================================================================

interface LoadingStateProps {
  /** Loading message */
  message?: string
  /** Icon/spinner to display */
  icon?: React.ReactNode
  /** Size variant */
  size?: 'sm' | 'md' | 'lg'
  /** Additional CSS classes */
  className?: string
}

const sizeConfig: Record<NonNullable<LoadingStateProps['size']>, { spinner: string; text: string }> = {
  sm: { spinner: 'h-4 w-4', text: 'text-xs' },
  md: { spinner: 'h-5 w-5', text: 'text-sm' },
  lg: { spinner: 'h-6 w-6', text: 'text-base' },
}

export function LoadingState({
  message = 'Loading...',
  icon,
  size = 'md',
  className,
}: LoadingStateProps) {
  const { spinner: spinnerSize, text: textSize } = sizeConfig[size]

  return (
    <div className={clsx('flex items-center py-8 pl-6', className)}>
      <div className="flex items-center space-x-3">
        {icon ?? (
          <div className={clsx('animate-spin rounded-full border-2 border-zinc-600 border-t-transparent', spinnerSize)} />
        )}
        <span className={clsx('text-foreground-muted', textSize)}>{message}</span>
      </div>
    </div>
  )
}

// ============================================================================
// Inline Loading State Component
// A compact horizontal loading indicator for inline use
// ============================================================================

const inlineSizeConfig: Record<'xs' | 'sm' | 'md' | 'lg', { spinner: string; text: string }> = {
  xs: { spinner: 'h-3.5 w-3.5', text: 'text-xs' },
  sm: { spinner: 'h-4 w-4', text: 'text-xs' },
  md: { spinner: 'h-5 w-5', text: 'text-sm' },
  lg: { spinner: 'h-6 w-6', text: 'text-base' },
}

interface InlineLoadingStateProps {
  /** Loading message */
  message?: string
  /** Size variant; xs is 14px, the size of an h-3.5 icon */
  size?: 'xs' | 'sm' | 'md' | 'lg'
  /** Additional CSS classes */
  className?: string
}

export function InlineLoadingState({
  message,
  size = 'md',
  className,
}: InlineLoadingStateProps) {
  const { spinner: spinnerSize, text: textSize } = inlineSizeConfig[size]

  return (
    <div className={clsx('flex items-center space-x-3', className)}>
      <div className={clsx('animate-spin rounded-full border-2 border-zinc-600 border-t-transparent', spinnerSize)} />
      {message && <span className={clsx('text-foreground-muted', textSize)}>{message}</span>}
    </div>
  )
}

// ============================================================================
// Error State Component
// An error state with optional retry action
// ============================================================================

interface ErrorStateProps {
  /** Error message */
  message?: string
  /** Icon to display */
  icon?: React.ReactNode
  /** Action button (e.g., retry, go back) */
  action?: React.ReactNode
  /** Additional CSS classes */
  className?: string
}

export function ErrorState({
  message = 'Something went wrong',
  icon,
  action,
  className,
}: ErrorStateProps) {
  return (
    <div className={clsx('flex flex-col items-center justify-center gap-4', className)}>
      {icon && (
        <div className="text-foreground-subtle">
          {icon}
        </div>
      )}
      <p className="text-foreground-muted">{message}</p>
      {action}
    </div>
  )
}

export default EmptyState
